import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  Divider,
  Drawer,
  Grid2 as Grid,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  Chip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import RefreshIcon from "@mui/icons-material/Refresh";

const EnterpriseNotariesGrid = ({ currentSession, onNotaryChanged }) => {
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [branchesFetching, setBranchesFetching] = useState(false);
  const [selectedNotary, setSelectedNotary] = useState(null);
  const [notaryDrawerOpen, setNotaryDrawerOpen] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [successText, setSuccessText] = useState("");
  const [selectedBranchFilter, setSelectedBranchFilter] = useState(null);

  const sortNotariesByNewest = useCallback(
    (notaries = []) =>
      [...notaries].sort(
        (a, b) => Number(b?.created_at || 0) - Number(a?.created_at || 0),
      ),
    [],
  );

  const toTitleCase = useCallback((value) => {
    const normalizedValue = String(value || "")
      .replace(/\s+/g, " ")
      .trim();

    if (!normalizedValue) return "N/A";

    return normalizedValue
      .split(" ")
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }, []);

  const getNotaryFullName = useCallback(
    (row) =>
      toTitleCase(
        row?.full_name ||
          `${row?.first_name || ""} ${row?.last_name || ""}`.trim(),
      ),
    [toTitleCase],
  );

  const formatDisplayDate = useCallback((value) => {
    if (!value || value === "N/A") return "N/A";

    if (typeof value === "number") {
      return new Date(value * 1000).toLocaleDateString("en-US");
    }

    const parsedDate = new Date(value);
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate.toLocaleDateString("en-US");
    }

    return String(value);
  }, []);

  const getNotaryStatus = useCallback((row) => {
    if (row?.on_site_registered) return "Active";
    return "Invitation Sent";
  }, []);

  const fetchNotaries = useCallback(async () => {
    setFetching(true);
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/enterprise/notaries/list`,
        { currentSession },
        { withCredentials: true },
      );

      setRows(sortNotariesByNewest(resp?.data?.data || []));
    } catch (error) {
      setErrorText(
        error?.response?.data?.message || "Failed to load enterprise notaries.",
      );
    } finally {
      setFetching(false);
    }
  }, [currentSession, sortNotariesByNewest]);

  const fetchBranches = useCallback(async () => {
    setBranchesFetching(true);
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/enterprise/branches/list`,
        { currentSession },
        { withCredentials: true },
      );

      setBranches(resp?.data?.data || []);
    } catch (error) {
      setErrorText(
        error?.response?.data?.message || "Failed to load enterprise branches.",
      );
    } finally {
      setBranchesFetching(false);
    }
  }, [currentSession]);

  useEffect(() => {
    fetchNotaries();
  }, [fetchNotaries]);

  useEffect(() => {
    fetchBranches();
  }, [fetchBranches]);

  useEffect(() => {
    if (!errorText && !successText) return;
    const timer = setTimeout(() => {
      setErrorText("");
      setSuccessText("");
    }, 5000);
    return () => clearTimeout(timer);
  }, [errorText, successText]);

  // const handleRemove = useCallback(
  //   async (email) => {
  //     setRemoveLoadingEmail(email);
  //     setErrorText("");
  //     setSuccessText("");
  //     try {
  //       const resp = await axios.post(
  //         `${process.env.REACT_APP_BASE_URL}/nocApi/enterprise/notaries/delete`,
  //         { email, currentSession },
  //         { withCredentials: true },
  //       );

  //       setRows((prev) => prev.filter((row) => row?.email !== email));
  //       if (selectedNotary?.email === email) {
  //         setSelectedNotary(null);
  //         setNotaryDrawerOpen(false);
  //       }
  //       setSuccessText(resp?.data?.message || "Notary removed successfully.");
  //       onNotaryChanged?.();
  //     } catch (error) {
  //       setErrorText(
  //         error?.response?.data?.message || "Failed to remove notary.",
  //       );
  //     } finally {
  //       setRemoveLoadingEmail("");
  //     }
  //   },
  //   [currentSession, onNotaryChanged, selectedNotary],
  // );

  const handleOpenNotaryDetails = useCallback((notary) => {
    setSelectedNotary(notary);
    setNotaryDrawerOpen(true);
  }, []);

  const detailFields = useMemo(
    () => [
      {
        label: "Branch",
        value:
          selectedNotary?.branch_name || selectedNotary?.branch_email || "N/A",
      },
      { label: "Email", value: selectedNotary?.email || "N/A" },
      { label: "Phone", value: selectedNotary?.phone || "N/A" },
      {
        label: "Commission #",
        value: selectedNotary?.commission_number || "N/A",
      },
      {
        label: "Commission Expiry",
        value: formatDisplayDate(selectedNotary?.commission_expiry),
      },
      { label: "Address", value: selectedNotary?.address || "N/A" },
      { label: "City", value: selectedNotary?.city || "N/A" },
      { label: "County", value: selectedNotary?.county || "N/A" },
      {
        label: "State",
        value: selectedNotary?.state
          ? String(selectedNotary.state).toUpperCase()
          : "N/A",
      },
      { label: "Zip", value: selectedNotary?.zip || "N/A" },
      {
        label: "Created At",
        value: formatDisplayDate(selectedNotary?.created_at),
      },
      {
        label: "Pending Jobs",
        value: selectedNotary?.pending_jobs ?? "N/A",
      },
      {
        label: "Completed Jobs",
        value: selectedNotary?.completed_jobs ?? "N/A",
      },
      {
        label: "Cancelled Jobs",
        value: selectedNotary?.cancelled_jobs ?? "N/A",
      },
      {
        label: "Total Worked Hours",
        value: selectedNotary?.total_worked_hours ?? "N/A",
      },
      {
        label: "Online Status",
        value: selectedNotary?.online_status || "N/A",
      },
      {
        label: "Last Notarization",
        value: selectedNotary?.last_notarization_time || "N/A",
      },
    ],
    [formatDisplayDate, selectedNotary],
  );

  const columns = useMemo(
    () => [
      {
        field: "full_name",
        headerName: "Name",
        flex: 1.4,
        minWidth: 220,
        valueGetter: (_, row) => getNotaryFullName(row),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.9,
        minWidth: 170,
        renderCell: (params) => {
          const isRegistered = getNotaryStatus(params?.row) === "Active";
          return (
            <Chip
              size="small"
              color={isRegistered ? "success" : "warning"}
              label={isRegistered ? "Active" : "Invitation Sent"}
            />
          );
        },
      },
      {
        field: "created_at",
        headerName: "Created At",
        flex: 0.8,
        minWidth: 130,
        valueGetter: (_, row) => formatDisplayDate(row?.created_at),
      },
    ],
    [formatDisplayDate, getNotaryFullName, getNotaryStatus],
  );

  const branchFilterOptions = useMemo(() => branches, [branches]);

  const getBranchLabel = useCallback(
    (branch) =>
      branch?.branch_name ||
      branch?.additional?.companyName ||
      branch?.email ||
      "",
    [],
  );

  const filteredRows = useMemo(() => {
    if (!selectedBranchFilter) {
      return rows;
    }

    return rows.filter((row) => {
      if (selectedBranchFilter?.branch_id && row?.branch_id) {
        return row.branch_id === selectedBranchFilter.branch_id;
      }

      return row?.branch_email === selectedBranchFilter?.email;
    });
  }, [rows, selectedBranchFilter]);

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: 3,
        minWidth: 0,
      }}
    >
      <CardContent sx={{ minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="h6" color="primary">
              Enterprise Notaries
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create and manage the organization notary pool for this enterprise
              account.
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
              ml: { xs: 0, md: "auto" },
            }}
          >
            <Autocomplete
              options={branchFilterOptions}
              freeSolo={false}
              value={selectedBranchFilter}
              onChange={(_, newValue) =>
                setSelectedBranchFilter(newValue || null)
              }
              disabled={branchesFetching}
              clearOnEscape
              isOptionEqualToValue={(option, value) => {
                if (option?.branch_id && value?.branch_id) {
                  return option.branch_id === value.branch_id;
                }
                return option?.email === value?.email;
              }}
              getOptionLabel={getBranchLabel}
              sx={{ minWidth: 240, width: { xs: "100%", sm: 280 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by branch"
                  helperText={branchesFetching ? "Loading branches..." : ""}
                />
              )}
            />

            <Tooltip title="Refresh Notaries">
              <span>
                <IconButton
                  onClick={fetchNotaries}
                  disabled={fetching}
                  color="primary"
                >
                  <RefreshIcon className={fetching ? "rotate" : ""} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {!!errorText && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorText}
          </Alert>
        )}
        {!!successText && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successText}
          </Alert>
        )}

        <Box>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            getRowId={(row) => row?.email || row?.notary_id}
            autoHeight
            disableRowSelectionOnClick
            disableColumnMenu
            onRowClick={(params) => handleOpenNotaryDetails(params.row)}
            pageSizeOptions={[5, 10, 25]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 5, page: 0 },
              },
              sorting: {
                sortModel: [{ field: "created_at", sort: "desc" }],
              },
            }}
            sx={{
              border: "1px solid #FCF2F0",
              "& .MuiDataGrid-row": {
                cursor: "pointer",
              },
              "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                outline: "none !important",
              },
              "& .MuiDataGrid-columnHeader:focus-within": {
                outline: "none !important",
              },
            }}
          />
        </Box>

        <Drawer
          anchor="right"
          open={notaryDrawerOpen}
          onClose={() => setNotaryDrawerOpen(false)}
        >
          <Box sx={{ width: { xs: "100vw", sm: 520 }, p: 3 }}>
            <Typography variant="h5" color="primary" gutterBottom>
              {getNotaryFullName(selectedNotary)}
            </Typography>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              <Chip
                size="small"
                color={
                  getNotaryStatus(selectedNotary) === "Active"
                    ? "success"
                    : "warning"
                }
                label={getNotaryStatus(selectedNotary)}
              />
              {selectedNotary?.on_site_registered && (
                <Chip
                  size="small"
                  label="On-Site Registered"
                  variant="outlined"
                />
              )}
              {selectedNotary?.bn_registered && (
                <Chip size="small" label="BN Registered" variant="outlined" />
              )}
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              {detailFields.map((field) => (
                <Grid key={field.label} size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    {field.label}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600, wordBreak: "break-word" }}
                  >
                    {field.value}
                  </Typography>
                </Grid>
              ))}
            </Grid>

            {/* <Box sx={{ mt: 4 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Remove this notary from the enterprise pool.
              </Typography>
              <LoadingButton
                variant="contained"
                color="error"
                fullWidth
                loading={removeLoadingEmail === selectedNotary?.email}
                disabled={!selectedNotary?.email}
                startIcon={<DeleteIcon />}
                onClick={() => handleRemove(selectedNotary?.email)}
              >
                Remove Notary
              </LoadingButton>
            </Box> */}
          </Box>
        </Drawer>
      </CardContent>
    </Card>
  );
};

export default EnterpriseNotariesGrid;
